import React from "react";

import { FreightMode, ShippingTypeValue } from "@assets/constants";
import { ExposedBookingCargoDetailsRes, ExposedContainerDataRes } from "@assets/types";
import { StringUtilities } from "@assets/utilities";
import Link from "@components/Link/link";
import Tooltip from "@components/Tooltip/tooltip";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { ActionTableJonar, Table, TableColumnShorthand } from "@samskip/frontend-components";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { FaBuffer, FaExclamationTriangle, FaTemperatureHigh } from "react-icons/fa";

type ContainerDetailsCellProps = {
  containerNumber: string;
  containerData: ExposedContainerDataRes;
  isBcBooking: boolean | null | undefined;
  shippingTypeValue?: string | null;
  freightMode?: string;
  marksAndNumbers: any;
};

type CargoRow = {
  id: string;
  description: string;
  quantity: number;
  packageCode: string;
  volume: string | number;
  weight: string | number;
  isStackable: string;
  isHazardous: string;
  temperature: string;
};

interface Mark {
  JobReference: string;
  Marks: string;
  Description: string;
}

export const ContainerDetailsCell: React.FC<ContainerDetailsCellProps> = ({
  containerNumber,
  containerData,
  isBcBooking,
  shippingTypeValue,
  freightMode,
  marksAndNumbers,
}) => {
  const { t } = useTranslation();

  const handleTemperature = (
    isRefrigerated: boolean | null,
    minTemp: number | null,
    maxTemp: number | null
  ): string => {
    let temperature = "";
    if (isRefrigerated) {
      minTemp && minTemp < 0 ? (temperature = `${minTemp}°C`) : (temperature = `+${minTemp}°C`);
      temperature = temperature.concat("/");
      maxTemp && maxTemp < 0
        ? (temperature = temperature.concat(`${maxTemp}°C`))
        : (temperature = temperature.concat(`+${maxTemp}°C`));
    } else temperature = i18n.t("TEXT_NO");
    return temperature;
  };

  const createCargoDataTableHeaders = () => {
    return [
      {
        id: "Description",
        name: t("LABEL_CARGO_DESCRIPTION"),
        className: "w-1/2",
      },
      {
        id: "QTY",
        name: t("LABEL_QUANTITY_ABBREV"),
      },
      {
        id: "PackageType",
        name: t("LABEL_PACKAGE"),
      },
      {
        id: "Volume",
        name: t("LABEL_VOLUME"),
      },
      {
        id: "Weight",
        name: t("LABEL_WEIGHT"),
      },
      {
        id: "Stackable",
        name: (
          <Tooltip childClassName="inline-flex" content={t("INFO_STACKABLE")} key="Stackable">
            <FaBuffer className="text-base" key="Stackable" />
          </Tooltip>
        ),
      },
      {
        id: "Hazardous",
        name: (
          <Tooltip childClassName="inline-flex" content={t("INFO_HAZARDOUS")} key="Hazardous">
            <FaExclamationTriangle className="text-base" key="Hazardous" />
          </Tooltip>
        ),
      },
      {
        id: "TemperatureControlled",
        name: (
          <Tooltip
            childClassName="inline-flex"
            content={t("INFO_TEMPERATURE_VALUES")}
            key="Temperature"
          >
            <FaTemperatureHigh className="text-base" key="Temperature" />
          </Tooltip>
        ),
      },
    ];
  };

  const createCargoDataTableHeadersLclLcl: TableColumnShorthand<CargoRow>[] = [
    {
      id: "Description",
      title: t("LABEL_CARGO_DESCRIPTION"),
      dataIndex: "description",
      className: "w-1/2",
    },
    {
      id: "QTY",
      title: t("LABEL_QUANTITY_ABBREV"),
      dataIndex: "quantity",
    },
    {
      id: "PackageType",
      title: t("LABEL_PACKAGE"),
      dataIndex: "packageCode",
    },
    {
      id: "Volume",
      title: t("LABEL_VOLUME"),
      dataIndex: "volume",
    },
    {
      id: "Weight",
      title: t("LABEL_WEIGHT"),
      dataIndex: "weight",
    },
    {
      id: "Stackable",
      title: (
        <Tooltip childClassName="inline-flex" content={t("INFO_STACKABLE")} key="Stackable">
          <FaBuffer className="text-base" key="Stackable" />
        </Tooltip>
      ),
      dataIndex: "isStackable",
    },
    {
      id: "Hazardous",
      title: (
        <Tooltip childClassName="inline-flex" content={t("INFO_HAZARDOUS")} key="Hazardous">
          <FaExclamationTriangle className="text-base" key="Hazardous" />
        </Tooltip>
      ),
      dataIndex: "isHazardous",
    },
    {
      id: "TemperatureControlled",
      title: (
        <Tooltip
          childClassName="inline-flex"
          content={t("INFO_TEMPERATURE_VALUES")}
          key="Temperature"
        >
          <FaTemperatureHigh className="text-base" key="Temperature" />
        </Tooltip>
      ),
      dataIndex: "temperature",
    },
  ];

  const createCargoDataTable = (details: ExposedBookingCargoDetailsRes[] | null) => {
    return details
      ? details.map((detail, index) => {
          return {
            id: `cargo-${index}`,
            cells: [
              detail.Description ?? "N/A",
              detail.Quantity ?? 0,
              detail.PackageCode ?? "N/A",
              detail?.Volume
                ? `${StringUtilities.numberCommaDecimal(detail?.Volume)}
                              ${detail.VolumeUOM}`
                : 0,
              detail?.Weight
                ? `${StringUtilities.numberCommaDecimal(detail?.Weight)}
                              ${detail.WeightUOM}`
                : 0,
              detail.IsStackable ? i18n.t("TEXT_YES") : i18n.t("TEXT_NO"),
              detail.IsHazardous ? i18n.t("TEXT_YES") : i18n.t("TEXT_NO"),
              handleTemperature(
                detail.IsRefrigerated,
                detail.MinTemperature,
                detail.MaxTemperature
              ),
            ],
          };
        })
      : [];
  };

  const createCargoDataTableLclLcl = (
    details: ExposedBookingCargoDetailsRes[] | null,
    marks: Mark[]
  ) => {
    return details
      ? details.map((detail, index) => {
          const matchingMark = marks.find((mark) => {
            const descriptions = (mark.Description ?? "")
              .split(",")
              .map((d) => d.trim().toLowerCase());
            const detailDesc = (detail.Description ?? "").toLowerCase();

            return descriptions.some((desc) => desc.includes(detailDesc));
          });

          return {
            id: `cargo-${index}`,
            description: detail.Description ?? "N/A",
            quantity: detail.Quantity ?? 0,
            packageCode: detail.PackageCode ?? "N/A",
            volume: detail?.Volume
              ? `${StringUtilities.numberCommaDecimal(detail?.Volume)} ${detail.VolumeUOM}`
              : 0,
            weight: detail?.Weight
              ? `${StringUtilities.numberCommaDecimal(detail?.Weight)} ${detail.WeightUOM}`
              : 0,
            isStackable: detail.IsStackable ? i18n.t("TEXT_YES") : i18n.t("TEXT_NO"),
            isHazardous: detail.IsHazardous ? i18n.t("TEXT_YES") : i18n.t("TEXT_NO"),
            temperature: handleTemperature(
              detail.IsRefrigerated,
              detail.MinTemperature,
              detail.MaxTemperature
            ),
            mark: matchingMark?.Marks ?? null,
          };
        })
      : [];
  };

  const createDataTable = (
    details: ExposedBookingCargoDetailsRes[] | null,
    marks: Mark[],
    detailIndex: number
  ) => {
    if (!details || detailIndex < 0 || detailIndex >= details.length) return [];

    const detail = details[detailIndex];

    let matchingMark: string | null = null;
    if (marks.length > 0) {
      const markEntry = marks[0];
      const descriptions = (markEntry.Description ?? "").split(",").map((d) => d.trim());
      const marksList = (markEntry.Marks ?? "").split(",").map((m) => m.trim());

      const index = descriptions.findIndex((desc) => desc.includes(detail.Description ?? ""));

      if (index !== -1 && index < marksList.length) {
        matchingMark = marksList[index];
      }
    }

    return [
      {
        id: `cargo-${detailIndex}`,
        description: detail.Description ?? "N/A",
        quantity: detail.Quantity ?? 0,
        packageCode: detail.PackageCode ?? "N/A",
        volume: detail?.Volume
          ? `${StringUtilities.numberCommaDecimal(detail?.Volume)} ${detail.VolumeUOM}`
          : 0,
        weight: detail?.Weight
          ? `${StringUtilities.numberCommaDecimal(detail?.Weight)} ${detail.WeightUOM}`
          : 0,
        isStackable: detail.IsStackable ? i18n.t("TEXT_YES") : i18n.t("TEXT_NO"),
        isHazardous: detail.IsHazardous ? i18n.t("TEXT_YES") : i18n.t("TEXT_NO"),
        temperature: handleTemperature(
          detail.IsRefrigerated,
          detail.MinTemperature,
          detail.MaxTemperature
        ),
        mark: matchingMark ?? null,
      },
    ];
  };

  const renderDetailTableByIndex = (detailIndex: number) => {
    return (
      <div className="my-6">
        <Table
          collapsible={{
            applicableRows: (row) => row.mark !== null,
            render: (row) => (
              <div className="py-4">
                {/* TODO: Replace with Label/WithLabel from the frontend component library once it's been updated for compatibility */}
                <label className="text-neutral-800 font-semibold">{t("LABEL_MARKS")}</label>
                {row.mark && (
                  <div className="pt-2 text-neutral-9 leading-5 text-sm font-normal">
                    {row.mark}
                  </div>
                )}
              </div>
            ),
            buttonMessage: t("LABEL_SHOW_MORE"),
            openedButtonMessage: t("LABEL_SHOW_LESS"),
            placement: "end",
          }}
          columns={createCargoDataTableHeadersLclLcl}
          getRowId={(r) => r.id}
          id="table-collapsible"
          rowSize="large"
          rows={createDataTable(containerData.Details, marksAndNumbers, detailIndex)}
        />
      </div>
    );
  };

  const renderDetailTable = () => {
    return (
      <div className="my-6">
        <ActionTableJonar
          className="divide-y divide-neutral-500"
          headers={createCargoDataTableHeaders()}
          id="CargoTable"
          shouldFade={false}
          tableData={createCargoDataTable(containerData.Details)}
        />
      </div>
    );
  };

  const renderDetailTableLclLcl = () => {
    return (
      <div className="my-6">
        <Table
          collapsible={{
            applicableRows: (row) => row.mark !== null,
            render: (row) => (
              <div className="py-4">
                {/* TODO: Replace with Label/WithLabel from the frontend component library once it's been updated for compatibility */}
                <label className="text-neutral-800 font-semibold">{t("LABEL_MARKS")}</label>
                {row.mark && (
                  <div className="pt-2 text-neutral-9 leading-5 text-sm font-normal">
                    {row.mark}
                  </div>
                )}
              </div>
            ),
            buttonMessage: t("LABEL_SHOW_MORE"),
            openedButtonMessage: t("LABEL_SHOW_LESS"),
            placement: "end",
          }}
          columns={createCargoDataTableHeadersLclLcl}
          getRowId={(r) => r.id}
          id="table-collapsible"
          rowSize="large"
          rows={createCargoDataTableLclLcl(containerData.Details, marksAndNumbers)}
        />
      </div>
    );
  };

  const bookingReferenceSection = containerData.Details?.map((detail, index) => (
    <div className="mt-6" key={`detail-${index}`}>
      {isBcBooking && (
        <div className="grid grid-cols-3 mb-4">
          <div className="flex items-center">
            <div className="rounded-full bg-jonar-blue h-6 w-6 text-white m-2 flex justify-center items-center">
              {index + 1}
            </div>
            <div className="flex flex-col">
              <label className="text-neutral-7">{t("LABEL_BOOKING_REFERENCE")}</label>
              {detail.BcBookingReference ? (
                <div>
                  <Link
                    href={`/singleBooking/${freightMode}/${detail.BcBookingReference}`}
                    icon={faArrowUpRightFromSquare}
                    target="_blank"
                  >
                    {detail.BcBookingReference}
                  </Link>
                </div>
              ) : (
                <div>N/A</div>
              )}
            </div>
          </div>
          <div>
            <label className="text-neutral-7">{t("LABEL_SHIPPER")}</label>
            <div className="text-neutral-9">{detail.BcBookingShipper ?? "N/A"}</div>
          </div>
          <div>
            <label className="text-neutral-7">{t("LABEL_CONSIGNEE")}</label>
            <div className="text-neutral-9">{detail.BcBookingConsignee ?? "N/A"}</div>
          </div>
        </div>
      )}
      {renderDetailTableByIndex(index)}
    </div>
  ));

  const containerDetails = () => {
    return (
      <div className="border-2 p-4 grid grid-cols-5">
        <div>
          <label className="text-neutral-7">{t("LABEL_CONTAINER_NUMBER")}</label>
          <div className="text-neutral-9">{containerNumber ?? "N/A"}</div>
        </div>
        <div>
          <label className="text-neutral-7">{t("LABEL_CONTAINER_TYPE")}</label>
          <div className="text-neutral-9">{containerData.ContainerType ?? "N/A"}</div>
        </div>
        <div>
          <label className="text-neutral-7">{t("LABEL_SEAL_NUMBER")}</label>
          <div className="text-neutral-9">{containerData.SealNumber ?? "N/A"}</div>
        </div>
        <div>
          <label className="text-neutral-7">{t("LABEL_VOLUME")}</label>
          <div className="text-neutral-9">
            {containerData.Details?.reduce((x, y) => {
              return x + (y.Volume ?? 0);
            }, 0)}
          </div>
        </div>
        <div>
          <label className="text-neutral-7">{t("LABEL_WEIGHT")}</label>
          <div className="text-neutral-9">
            {containerData.Details?.reduce((x, y) => {
              return x + (y.Weight ?? 0);
            }, 0)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {freightMode == FreightMode.SH && shippingTypeValue === ShippingTypeValue.LL && (
        <>{renderDetailTableLclLcl()}</>
      )}
      {freightMode == FreightMode.SH &&
        (shippingTypeValue === ShippingTypeValue.LF ||
          shippingTypeValue === ShippingTypeValue.FL) && (
          <>
            {containerDetails()}
            {bookingReferenceSection}
          </>
        )}
      {freightMode == FreightMode.SH && shippingTypeValue === ShippingTypeValue.FF && (
        <>
          {containerDetails()}
          {renderDetailTable()}
        </>
      )}
      {freightMode == FreightMode.AI && shippingTypeValue === ShippingTypeValue.LL && (
        <>{renderDetailTable()}</>
      )}
    </>
  );
};

ContainerDetailsCell.displayName = "ContainerDetailsCell";

export default ContainerDetailsCell;
